// ============================================================
// Authentication Layout
// ============================================================
// Centered card layout for login and forgot password pages.
// Supports dynamic school branding (logo, name, login image) from public school configuration.

import { Box, Typography } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { tokens } from '@/theme/tokens';
import { usePublicSchool } from '@/context/PublicSchoolContext';

export function AuthLayout() {
  const { publicSchoolData } = usePublicSchool();

  const schoolName = publicSchoolData?.school_name || 'Shreeji';
  const loginImage = publicSchoolData?.login_image || '/images/school_bg.png';
  const primaryColor = publicSchoolData?.primary_color || tokens.colors.primary.main;
  const tagline = publicSchoolData?.tagline || 'The ultimate Smart School ERP to streamline operations, empower educators, and drive student success.';

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: tokens.colors.background }}>
      {/* Left side: Background Image with Gradient Overlay */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          backgroundColor: primaryColor || '#0f172a',
          backgroundImage: `linear-gradient(135deg, rgba(15, 23, 42, 0.55) 0%, rgba(15, 23, 42, 0.85) 100%), url("${loginImage}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          position: 'relative',
          p: 8,
          color: 'white',
        }}
      >
        <Box sx={{ maxWidth: 600 }}>
          <Box sx={{ display: 'inline-block', px: 2, py: 0.5, borderRadius: 4, backgroundColor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} letterSpacing="0.05em">
              WELCOME TO
            </Typography>
          </Box>
          <Typography variant="h2" fontWeight={800} sx={{ mb: 2, letterSpacing: '-0.02em', lineHeight: 1.1, textShadow: '0 4px 20px rgba(0,0,0,0.5)' }}>
            {schoolName}
          </Typography>
          <Typography variant="h6" fontWeight={500} sx={{ opacity: 0.95, lineHeight: 1.6, textShadow: '0 2px 10px rgba(0,0,0,0.5)', maxWidth: 500 }}>
            {tagline}
          </Typography>
        </Box>
      </Box>

      {/* Right side: Login Form */}
      <Box
        sx={{
          width: { xs: '100%', md: 480, lg: 540 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 4, sm: 6, md: 8 },
          py: 4,
          backgroundColor: '#ffffff',
          boxShadow: { md: '-20px 0 50px rgba(0,0,0,0.08)' },
          zIndex: 1,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          {/* Brand Header */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 220,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1,
              }}
            >
              <Box
                component="img"
                src="/shreeji-logo-clean.png"
                alt="ShreeJi"
                sx={{
                  width: '100%',
                  maxHeight: 75,
                  objectFit: 'contain',
                }}
              />
            </Box>

          </Box>

          {/* Page Content */}
          <Outlet />

          {/* Powered By Footer */}
          <Box sx={{ mt: 5, textAlign: 'center' }}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: 0.4,
              }}
            >
              Powered By Harivansh Digital
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

